import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import currency from 'currency.js'

import Config from '@glass-designer/config'
import OrderItem from '@glass-designer/model/orderItem'
import ShapeCutout from '@glass-designer/model/shapeCutout'
import SVGHelper from '@glass-designer/helpers/svgHelper'
import Utils from '@/utils/utils'
import MathsUtils from '@/utils/mathsUtils'
import FilterUtils from '@/utils/filterUtils'
import CircleShape from '@glass-designer/model/shapes/circleShape'
import OvalShape from '@glass-designer/model/shapes/ovalShape'
import GlassShape from '@glass-designer/model/glassShape'
import AssemblyGroup from '@glass-designer/model/assemblyGroup'

import GlassDesignerTypes from '@glass-designer/types'
import { OrderTypes, InventoryStockTypes, OrderEditorEdgeworkSettingTypes, OrderItemEdgeworkTypes, PriceTypes, UOMTypes, AccountSettingKeys } from '@/store/types'
import { CutoutPolishFilterTypes, OrderEditorCutoutTypes, OrderEditorCutoutTypeLookup, GlassTypes, StockFaceTypeLookup, StockGrainDirectionTypeLookup } from '@/store/types'


/**
 * Order Helper
 * Creates / Updates the order items based on the glass panels in the editor scene
 * Service items are based on configuration data from the server to control how service items are added to glass items
 */
export default class OrderHelper {
  constructor(editor) {
    this.editor = editor

    this.releasedOrderItemList = []
    this.editorConfigurationData = {}
  }


  /**
   * Builds the list of objects representing OrderItem model objects for the order
   * If OrderItems have already been created on the server (when updating orders) this attempts to reuse the item IDs
   * OrderItem list return is flattened to match the structure used by OrderItemList
   * Note: The OrderItem lists in the order & GlassShapes are updated by this function
   * 
   * @param {Order} order - Order object containing the order details
   * @param {Array<GlassShape>} glassPanelList - List of GlassShape objects in the scene, each representing a glass panel item
   * @param {Array<AssemblyGroup>} assemblyGroupList - List of AssemblyGroup objects in the scene, each representing an assembly group item
   * @param {Object} editorConfigurationData - Object containing all the editor configuration data loaded from the server
   * @return {Promise<>} Returns a promise resolved on completion
   */
  updateOrderItemList(order, glassPanelList, assemblyGroupList, editorConfigurationData) {
    return new Promise(async (resolve, reject) => {
      try {
        this.editorConfigurationData = editorConfigurationData

        // Move to released order items & clear order items
        // Released items will be reused if possible
        this.releasedOrderItemList = order.orderItems
        order.orderItems = []

        for(let glassPanel of glassPanelList) {
          // Ignore if part of an assembly group
          if(glassPanel.getParentObject())
            continue

          await this._generateGlassOrderItem(order, glassPanel)
        }


        // Assemblies
        for(let assemblyGroup of assemblyGroupList) {
          // Ignore if part of another assembly group
          // The top level assembly group will be processed & recursively go through everything
          if(assemblyGroup.getParentObject())
            continue

          await this._generateAssemblyOrderItem(order, assemblyGroup)
        }


        // Build the lookup list for batching price queries & run the request
        // Collate into same data groups to reduce server hit
        var priceLookupList = []
        order.orderItems.forEach(orderItem => {
          let existingLookup = priceLookupList.find(existing => {
            return (
              existing.stockId == orderItem.stockId &&
              existing.stockPriceGroupId == orderItem.stockPriceGroupId &&
              existing.thickness == orderItem.thickness &&
              existing.priceType == PriceTypes.customer &&
              existing.salesRateId == order.salesRateId &&
              existing.contactId == order.contactId
            )
          })
          if(existingLookup) {
            existingLookup.idList.push(orderItem.id)
          } else {
            priceLookupList.push({
              id: orderItem.id,
              idList: [orderItem.id],
              stockId: orderItem.stockId,
              stockPriceGroupId: orderItem.stockPriceGroupId,
              thickness: orderItem.thickness,
              priceType: PriceTypes.customer,
              salesRateId: order.salesRateId,
              contactId: order.contactId
            })
          }
        })

        let priceResults = await this.editor.component.$apiService.ordersService.getContactStockBatchLookup(priceLookupList)
        if(_.isArray(priceResults.data)) {
          priceResults.data.forEach(priceResult => {
            let lookup = priceLookupList.find(item => item.id == priceResult.id)
            if(lookup) {
              order.orderItems.filter(item => lookup.idList.indexOf(item.id) != -1).forEach(orderItem => {
                orderItem.priceError = ''

                if(priceResult.price && !priceResult.price.error) {
                  if(!orderItem.unitPriceOverridden)
                    orderItem.unitPrice = priceResult.price.value
                  orderItem.originalUnitPrice = priceResult.price.value
                  orderItem.minChargeInvoice = priceResult.price.minChargeInvoice
                  orderItem.minChargePrice = priceResult.price.minChargePrice
                  orderItem.minChargeItemPrice = priceResult.price.minChargeItemPrice
                  orderItem.minChargeQuantity = priceResult.price.minChargeQuantity
                }
                if(priceResult.price && priceResult.price.error) {
                  orderItem.priceError = priceResult.price.error
                }
                if(priceResult.stock) {
                  orderItem.stock.id = orderItem.stockId
                  orderItem.stock.uomType = priceResult.stock.uomType
                  orderItem.stock.code = priceResult.stock.code
                  orderItem.stock.name = priceResult.stock.name

                  if(priceResult.servicePriceRange) {
                    orderItem.servicePriceRange = _.clone(priceResult.servicePriceRange || {})
                    orderItem.servicePriceRangeId = priceResult.servicePriceRange.id
                  }

                  if((orderItem.description||'') == '') {
                    orderItem.description = priceResult.stock.name
                  }
                }
              })
            }
          })
        }

        // Add in all the non-editor order items currently in the order
        this.releasedOrderItemList.filter(orderItem => !orderItem.isEditorGeneratedItem).forEach(orderItem => {
          
          // If this is a child of an order item that no longer exists, ignore it
          let parentItem = order.getOrderItemById(orderItem.parentOrderItemId)
          if(!parentItem && orderItem.parentOrderItemId)
            return

          // Don't add in the min charge items from before editing
          if(orderItem.isMinPriceItem || orderItem.isMinPriceSingleItem)
            return

          order.addOrderItem(orderItem)
        })

        // Calculate pricing now the unit price details have been queried
        // Updates min charge quantity, min charge items, tax, order total
        this._calculatePricing(order)

        // Update order totals
        this._calculateOrderTotals(order)

        // Update approval items for review sidebar
        this._calculateReviewApprovalItems(order)

        return resolve()

      } catch(err) {
        return reject(err)
      }
    })
  }


  /**
   * Generate a glass order item for the given glass panel
   * 
   * @returns {Promise<Object>} Returns the OrderItem object created for the glass panel
   */
  async _generateGlassOrderItem(order, glassPanel, parentOrderItemId = null) {
    // Calculate size of the sheet based on cutting area
    // Rounded to nearest mm
    // JB: 18/01/2022: Rounding up (previously) does not work & causes issues
    var glassCutBounds = glassPanel.calculateGlassCutBounds()
    var panelWidth = Math.round(glassCutBounds.max.x - glassCutBounds.min.x)
    var panelHeight = Math.round(glassCutBounds.max.y - glassCutBounds.min.y)
    var isSplitSheetItem = false

    // If stock linked, use glass level info
    if(glassPanel.isStockLinked() && glassPanel.itemData.glassLevel) {
      panelWidth = glassPanel.itemData.glassLevel.width
      panelHeight = glassPanel.itemData.glassLevel.height

      // If it contains split lines, mark the item as a split sheet item
      if(glassPanel.getSplitLineList().length > 0)
        isSplitSheetItem = true
    }

    // Build the editorData & shapeData for the glass panel
    // These get stored with the OrderItem record for persistence & production module usage
    var shapeSerializedData = glassPanel.serialize()
    var editorData = {
      version: Config.Settings.orderEditorDataVersion,
      data: shapeSerializedData
    }
    var shapeData = glassPanel.generateShapeData()
    var shapeDataHash = await glassPanel.generateShapeLayoutHash(shapeData, panelWidth, panelHeight)

    // Generate the production editor errors
    let productionEditorError = this._calculateProductionEditorErrors(glassPanel)

    // Main glass item
    var glassItem = this._createOrderItem(order, {
      stockType: InventoryStockTypes.glass,
      stockId: glassPanel.itemData.stockId,
      parentOrderItemId: parentOrderItemId,
      stockPriceGroupId: glassPanel.itemData.stockPriceGroupId,
      description: glassPanel.itemData.description,
      mark: glassPanel.itemData.mark,
      notes: glassPanel.itemData.notes,
      quantity: glassPanel.itemData.quantity,
      units: Utils.roundQuantityValue(Utils.convertMillimetreSqToMetreSq(panelWidth * panelHeight)),
      unitPrice: glassPanel.itemData.unitPriceOverridden ? glassPanel.itemData.unitPrice : 0,
      unitPriceOverridden: glassPanel.itemData.unitPriceOverridden,
      glassHeight: panelHeight,
      glassWidth: panelWidth,
      thickness: glassPanel.getThickness(),
      grainDirection: glassPanel.itemData.grainDirection,
      stockFaceSideUpType: glassPanel.itemData.stockFaceSideUpType,
      isSplitSheetItem: isSplitSheetItem,
      glassLevelId: glassPanel.itemData.glassLevelId,
      glassLevel: glassPanel.itemData.glassLevelId ? glassPanel.itemData.glassLevel : null,
      editorData: editorData,
      shapeData: shapeData,
      shapeDataHash: shapeDataHash,
      productionEditorError: productionEditorError
    }, {
      editorType: GlassDesignerTypes.EditorOrderItemDataTypes.glassShape,
      glassShapeId: glassPanel.id
    })

    if(!glassItem)
      return Promise.resolve(null)

    // Glass Shape Service
    this._generateShapeTypeService(order, glassItem, glassPanel)

    // Minimum Edgework / Edgework service items
    this._generateEdgeworkServices(order, glassItem, glassPanel)

    // Service: Cutout list
    this._generateCutoutServices(order, glassItem, glassPanel)

    // Service: Radius cut list
    this._generateRadiusCutServices(order, glassItem, glassPanel)

    // Service: Panel Items
    this._generatePanelItemServices(order, glassItem, glassPanel)

    // Service: Split list
    this._generateSplitLineServices(order, glassItem, glassPanel)

    // Service: Stamps
    this._generateStampServices(order, glassItem, glassPanel)

    // Generate the SVG display data for the editor glass item
    this._generateGlassItemSVGDisplayData(glassItem, glassPanel)

    return Promise.resolve(glassItem)
  }


  /**
   * Generate the assembly order item for the given assembly
   * Recursively generates the order items for all child items in the assembly group
   * 
   * @return {Promise<Object>} Returns the OrderItem object created for the assembly group
   */
  async _generateAssemblyOrderItem(order, assemblyGroup, parentOrderItemId = null) {
    var assemblySize = assemblyGroup.calculateLargestSize()

    var panelHeight = assemblySize.height
    var panelWidth = assemblySize.width

    var assemblySerializedData = assemblyGroup.serialize()
    var editorData = {
      version: Config.Settings.orderEditorDataVersion,
      data: assemblySerializedData
    }
    var shapeData = {}
    var shapeDataHash = ''

    // Generate the production editor errors
    let productionEditorError = ''
    
    // Generate top level assembly order item
    var assemblyItem = this._createOrderItem(order, {
      stockType: InventoryStockTypes.assembly,
      stockId: assemblyGroup.itemData.stockId,
      parentOrderItemId: parentOrderItemId,
      stockPriceGroupId: assemblyGroup.itemData.stockPriceGroupId,
      description: assemblyGroup.itemData.description,
      mark: assemblyGroup.itemData.mark,
      notes: assemblyGroup.itemData.notes,
      quantity: assemblyGroup.itemData.quantity,
      units: Utils.roundQuantityValue(Utils.convertMillimetreSqToMetreSq(panelWidth * panelHeight)),
      unitPrice: assemblyGroup.itemData.unitPriceOverridden ? assemblyGroup.itemData.unitPrice : 0,
      unitPriceOverridden: assemblyGroup.itemData.unitPriceOverridden,
      glassHeight: panelHeight,
      glassWidth: panelWidth,
      editorData: editorData,
      shapeData: shapeData,
      shapeDataHash: shapeDataHash,
      productionEditorError: productionEditorError
    }, {
      editorType: GlassDesignerTypes.EditorOrderItemDataTypes.assemblyGroup,
      assemblyGroupId: assemblyGroup.id
    })

    if(!assemblyItem)
      return Promise.resolve(null)

    // Update units
    assemblyGroup.updateAssemblyItemDetails();

    // Go through children & run the generate order item function for each child
    var childList = assemblyGroup.getChildObjectList()

    for(let assemblyChildOrderItem of assemblyGroup.itemData.assemblyOrderItemList) {

      // Find the associated shape in the assembly with the order item
      let childObject = childList.find(obj => obj.assemblyLinkUUID == assemblyChildOrderItem.assemblyLinkUUID)
      if(childObject) {
        if(childObject instanceof GlassShape) {
          // Glass Shape
          await this._generateGlassOrderItem(order, childObject, assemblyItem.id)
        }
        if(childObject instanceof AssemblyGroup) {
          // Assembly group
          await this._generateAssemblyOrderItem(order, childObject, assemblyItem.id)
        }
      } else {
        // Support root general items
        if(assemblyChildOrderItem.stockType == InventoryStockTypes.general && assemblyChildOrderItem.parentOrderItemId == null) {
          // General item
          await this._generateAssemblyGeneralOrderItem(order, assemblyGroup, assemblyChildOrderItem, assemblyItem.id)          
        }
      }
    }

    // Generate the SVG display data for the assembly group item
    this._generateAssemblyItemSVGDisplayData(assemblyItem, assemblyGroup)

    return Promise.resolve(assemblyItem)
  }


  /**
   * Generate a general child order item for a given assembly
   * 
   * @return {Promise<Object>} Returns the OrderItem object created for the assembly group
   */
  async _generateAssemblyGeneralOrderItem(order, assemblyGroup, orderItemInfo, parentOrderItemId = null) {
    var editorData = {
      version: Config.Settings.orderEditorDataVersion,
      data: {
        assemblyLinkUUID: orderItemInfo.assemblyLinkUUID
      }
    }
    var shapeData = {}
    var shapeDataHash = ''

    // Generate general order item
    var generalItem = this._createOrderItem(order, {
      stockType: InventoryStockTypes.general,
      stockId: orderItemInfo.stockId,
      parentOrderItemId: parentOrderItemId,
      stockPriceGroupId: orderItemInfo.stockPriceGroupId,
      description: orderItemInfo.description,
      mark: orderItemInfo.mark,
      notes: orderItemInfo.notes,
      quantity: orderItemInfo.quantity,
      units: orderItemInfo.units,
      unitPrice: orderItemInfo.unitPriceOverridden ? orderItemInfo.unitPrice : 0,
      unitPriceOverridden: orderItemInfo.unitPriceOverridden,
      glassHeight: 0,
      glassWidth: 0,
      //thickness: glassPanel.getThickness(),
      editorData: editorData,
      shapeData: shapeData,
      shapeDataHash: shapeDataHash,
      productionEditorError: ''
    }, {
      editorType: GlassDesignerTypes.EditorOrderItemDataTypes.generalItem,
      assemblyGroupId: assemblyGroup.id,
      assemblyLinkUUID: orderItemInfo.assemblyLinkUUID
    })

    if(!generalItem)
      return Promise.resolve(null)

    return Promise.resolve(generalItem)
  }


  /**
   * Generate the glass service item for the shape type
   * Based on configuration data from the server
   * Returns the OrderItem object if created
   */
  _generateShapeTypeService(order, glassOrderItem, glassPanel) {
    let glassShapePriceTypeId = glassPanel.calculateShapePriceTypeId()

    var stockId = null
    if(this.editorConfigurationData.shapePriceTypeList) {
      let shapePriceType = this.editorConfigurationData.shapePriceTypeList.find(priceType => priceType.id == glassShapePriceTypeId)
      if(shapePriceType && shapePriceType.stockId)
        stockId = parseInt(shapePriceType.stockId || 0)
    }

    if(!stockId)
      return null

    let stockItem = this.editorConfigurationData.stockList.find(stock => stock.id == stockId)
    if(!stockItem)
      return null

    var serviceItem = this._createOrderItem(order, {
      parentOrderItemId: glassOrderItem.id,
      stockType: InventoryStockTypes.glassService,
      stockId: stockId,
      stockPriceGroupId: glassOrderItem.stockPriceGroupId,
      description: stockItem.description,
      quantity: glassOrderItem.quantity,
      units: 1,
      thickness: glassOrderItem.thickness
    }, {
      editorType: GlassDesignerTypes.EditorOrderItemDataTypes.shapeService,
      stockId: stockId,
      glassShapeId: glassPanel.id
    })

    return serviceItem
  }


  /**
   * Generate the edgework service items for the glass shape
   * Generates minimum edgework services if no edgework set & order settings have minimum config set
   */
  _generateEdgeworkServices(order, glassOrderItem, glassPanel) {
    // Go through the valid edgework edges on the glass panel
    // Based around pre-cutout shape of the glass (but after straight line cuts)

    // Calculate edgework segment list
    var edgeworkEdgeInfo = glassPanel.calculateEdgeworkEdgeList()

    // Calculate total potential length that can have edgework
    // We do this as multiple segments can be on a cutting line (eg, around a cutout), but we have to edgework the entire side pre-waterjet cutouts
    var totalEdgeworkLengthAvailable = 0    // In metres
    let cuttingLineList = glassPanel.calculateCuttingLineList()

    cuttingLineList.forEach(cuttingLine => {
      totalEdgeworkLengthAvailable += Utils.convertMillimetreToMetre(cuttingLine.startPosition.distanceTo(cuttingLine.endPosition))
    })

    // Add arcs on the outside of the shape as available for edgework
    glassPanel.segmentList.filter(segment => segment.type == GlassDesignerTypes.SegmentTypes.arc && segment.isEdgeworkAvailable).forEach(segment => {
      totalEdgeworkLengthAvailable += Utils.convertMillimetreToMetre(segment.getLength())
    })

    // Apply minimum edgeworks & collate into groups
    var edgeworkGroups = []
    edgeworkEdgeInfo.edgeworkChargeList.forEach(edgeInfo => {
      let edgeLengthMetres = MathsUtils.roundNumber(Utils.convertMillimetreToMetre(edgeInfo.length), 2)

      var orderEditorEdgeworkConfigId = edgeInfo.orderEditorEdgeworkConfigId
      if(orderEditorEdgeworkConfigId) {
        let edgeworkInfo = this.editorConfigurationData.edgeworkList.find(edgework => edgework.id == orderEditorEdgeworkConfigId)
        if(!edgeworkInfo)
          return

        let serviceName = this._generateEdgeworkServiceName(edgeworkInfo.name, edgeworkInfo.settingType, edgeInfo.edgeworkValue)

        var existingEdgework = edgeworkGroups.find(edgework => edgework.id == orderEditorEdgeworkConfigId && edgework.name == serviceName)
        if(!existingEdgework) {
          existingEdgework = {
            id: orderEditorEdgeworkConfigId,
            name: serviceName,
            stockId: edgeInfo.edgeworkStockId,
            units: edgeLengthMetres,
            edgeworkFlagList: [edgeInfo.edgeworkEdgeFlag],
            cutLineList: [{
              length: edgeLengthMetres,
              bounds: edgeInfo.cutLineBounds.clone()
            }],
          }
          edgeworkGroups.push(existingEdgework)
        } else {
          existingEdgework.units += edgeLengthMetres
          existingEdgework.edgeworkFlagList.push(edgeInfo.edgeworkEdgeFlag)

          // If a circle/oval shape, collate edgework into a single bounds region
          if((glassPanel instanceof CircleShape || glassPanel instanceof OvalShape) && existingEdgework.cutLineList.length > 0) {
            var cutBounds = existingEdgework.cutLineList[0].bounds
            existingEdgework.cutLineList[0].length += edgeLengthMetres
            cutBounds.union(edgeInfo.cutLineBounds)

            // Force all round for circles/ovals
            existingEdgework.edgeworkFlagList = [OrderItemEdgeworkTypes.allRound]

          } else {
            existingEdgework.cutLineList.push({
              length: edgeLengthMetres,
              bounds: edgeInfo.cutLineBounds.clone()
            })
          }
        }
      }
    })

    // Create edgework service items for each collated group
    edgeworkGroups.forEach(edgeInfo => {
      if(edgeInfo.units <= 0)
        return

      // If too many edgework flags (eg, shorts around an oblong shape), just clear it
      if(edgeInfo.edgeworkFlagList.length > 10)
        edgeInfo.edgeworkFlagList = []


      // Check if edgework if all around the shape, change edgeworkFlagList to 'allRound'
      if(edgeInfo.units > totalEdgeworkLengthAvailable * 0.99)
        edgeInfo.edgeworkFlagList = [OrderItemEdgeworkTypes.allRound]
      
      this._createOrderItem(order, {
        parentOrderItemId: glassOrderItem.id,
        stockType: InventoryStockTypes.glassService,
        stockId: edgeInfo.stockId,
        stockPriceGroupId: glassOrderItem.stockPriceGroupId,
        description: edgeInfo.name,
        quantity: glassOrderItem.quantity,
        units: edgeInfo.units,
        thickness: glassOrderItem.thickness,
        edgeworkFlagList: _.compact(edgeInfo.edgeworkFlagList)
      }, {
        editorType: GlassDesignerTypes.EditorOrderItemDataTypes.edgeworkService,
        stockId: edgeInfo.stockId,
        glassShapeId: glassPanel.id
      }, {
        cutLineList: edgeInfo.cutLineList
      })
    })
  }


  /**
   * Generate the edgework service name with value & setting type
   * Used for service item in order & diagram
   */
  _generateEdgeworkServiceName(edgeworkName, settingType, value) {
    let edgeworkPrecision = 2
    let edgeworkDisplayValue = Number.parseFloat(MathsUtils.roundNumber(value, edgeworkPrecision)).toFixed(edgeworkPrecision)

    var serviceName = edgeworkName
    if(settingType == OrderEditorEdgeworkSettingTypes.angle)
      serviceName += ' ' + edgeworkDisplayValue + ' DEG'
    else if(settingType == OrderEditorEdgeworkSettingTypes.size)
      serviceName += ' ' + edgeworkDisplayValue + ' MM'      

    return serviceName
  }


  /**
   * Generate the service items for cutouts in a glass panel
   */
  _generateCutoutServices(order, glassOrderItem, glassPanel) {
    let cutoutList = glassPanel.calculateCNCCutoutList()
    var usedShapeGroupIdList = []
    
    // Go through all cutouts in the glass panel
    var cutoutItemList = []
    cutoutList.forEach(shapeCutout => {
      let editorStock = this._findCutoutStockItem(shapeCutout)
      let cutoutStockItems = []

      if(editorStock)
        cutoutStockItems = [editorStock]

      // Find shape groups that are linked to the cutout
      // If stock overrides exist, use those stock items instead
      let shapeGroup = this._findShapeGroupForCNCCutout(glassPanel, shapeCutout)
      if(shapeGroup) {
        if(shapeGroup.isCutoutStockOverride) {
          // Only use overrides if not already applied for the group
          if(!usedShapeGroupIdList.includes(shapeGroup.id)) {
            cutoutStockItems = shapeGroup.cutoutStockOverrideList.map(stock => {
              return {
                stockId: stock.id
              }
            })
          } else {
            cutoutStockItems = []
          }

          // Store this shape group in a list, so we only use the overrides once
          // Occurs if multiple cutouts in the shape are linked to the same group which has stock overrides
          usedShapeGroupIdList.push(shapeGroup.id)
        }
      }

      cutoutStockItems.forEach(cutoutStockItem => {
        // Get the stock details
        let stockItem = this.editorConfigurationData.stockList.find(stock => stock.id == cutoutStockItem.stockId)
        if(stockItem) {

          // Generate a description for the cutout to be used in the order item
          // This is used for cutouts to indicate type / cut radius / size, etc to production staff
          var cutoutDescription = shapeCutout.generateDescription(cutoutStockItem, stockItem)
          var cutoutReviewDetails = shapeCutout.generateReviewDetails(glassPanel, shapeGroup)

          // Add the cutout to the list
          var cutCutout = Utils.findByMatchingProperties(cutoutItemList, {
            type: shapeCutout.type,
            stockType: stockItem.type,
            stockId: stockItem.id,              
            description: cutoutDescription
          })
          if(cutCutout) {
            cutCutout.units++
            cutCutout.cutoutBoundsList.push(shapeCutout.bounds.clone())
            cutCutout.cutoutDetailsItemList.push(cutoutReviewDetails)
          } else {
            cutoutItemList.push({
              type: shapeCutout.type,
              stockType: stockItem.type,
              stockId: stockItem.id,              
              description: cutoutDescription,
              units: 1,
              cutoutBoundsList: [shapeCutout.bounds.clone()],
              cutoutDetailsItemList: [cutoutReviewDetails]
            })
          }
        } else {
          console.error(`[CUTOUT] Could not find stock ${cutoutStockItem.stockId} in stock list for cutout`)
        }
      })
    })

    // Create service items for collated cutout objects
    cutoutItemList.forEach(cutoutItem => {

      this._createOrderItem(order, {
        parentOrderItemId: glassOrderItem.id,
        stockType: cutoutItem.stockType,
        stockId: cutoutItem.stockId,
        stockPriceGroupId: glassOrderItem.stockPriceGroupId,
        description: cutoutItem.description,
        quantity: glassOrderItem.quantity,
        units: cutoutItem.units,
        thickness: glassOrderItem.thickness
      }, {
        editorType: GlassDesignerTypes.EditorOrderItemDataTypes.cutoutService,
        cutoutType: cutoutItem.type,
        stockId: cutoutItem.stockId,
        glassShapeId: glassPanel.id
      }, {
        cutoutBoundsList: cutoutItem.cutoutBoundsList,
        cutoutDetailsItemList: cutoutItem.cutoutDetailsItemList
      })
    })    
  }


  /**
   * Generate the radius cut service list
   * Only processes corner radiuses that are not associated with cutouts already
   */
  _generateRadiusCutServices(order, glassOrderItem, glassPanel) {
    // Get list of radius cuts in the glass panel
    let radiusCutList = glassPanel.calculateRadiusCutList()

    var cutoutItemList = []
    radiusCutList.forEach(radiusCut => {

      let editorStock = this._findCutoutStockItem({
        type: OrderEditorCutoutTypes.radiusCut,
        isCutoutPolished: radiusCut.isPolished,
        maxDimension: radiusCut.radius
      })

      if(editorStock) {
        // Get the stock details
        let stockItem = this.editorConfigurationData.stockList.find(stock => stock.id == editorStock.stockId)
        if(stockItem) {

          // Generate a description for the cutout to be used in the order item
          // This is used for cutouts to indicate type / cut radius / size, etc to production staff
          var cutoutDescription = radiusCut.generateDescription(editorStock, stockItem)

          // Add the cutout to the list
          var cutCutout = Utils.findByMatchingProperties(cutoutItemList, {
            type: OrderEditorCutoutTypes.radiusCut,
            stockType: stockItem.type,
            stockId: stockItem.id,              
            description: cutoutDescription
          })
          if(cutCutout) {
            cutCutout.units++
            cutCutout.cutoutBoundsList.push(radiusCut.bounds.clone())
          } else {
            cutoutItemList.push({
              type: OrderEditorCutoutTypes.radiusCut,
              stockType: stockItem.type,
              stockId: stockItem.id,              
              description: cutoutDescription,
              units: 1,
              cutoutBoundsList: [radiusCut.bounds.clone()]
            })
          }
        } else {
          console.error(`[CUTOUT] Could not find stock ${editorStock.stockId} in stock list for radius cut`)
        }
      } else {
        console.error(`[CUTOUT] Could not find cutout config for cutout`)
      }
    })

    // Create service items for collated radius cut objects
    cutoutItemList.forEach(cutoutItem => {

      this._createOrderItem(order, {
        parentOrderItemId: glassOrderItem.id,
        stockType: cutoutItem.stockType,
        stockId: cutoutItem.stockId,
        stockPriceGroupId: glassOrderItem.stockPriceGroupId,
        description: cutoutItem.description,
        quantity: glassOrderItem.quantity,
        units: cutoutItem.units,
        thickness: glassOrderItem.thickness
      }, {
        editorType: GlassDesignerTypes.EditorOrderItemDataTypes.cutoutService,
        cutoutType: cutoutItem.type,
        stockId: cutoutItem.stockId,
        glassShapeId: glassPanel.id
      }, {
        cutoutBoundsList: cutoutItem.cutoutBoundsList
      })
    })
  }


  /**
   * Generate the services for any panel items linked to services
   */
  _generatePanelItemServices(order, glassOrderItem, glassPanel) {
    let panelItems = glassPanel.getPanelItemList()

    // Ignore special stamp items
    panelItems = panelItems.filter(panelItem => !panelItem.isOrderStampItem)

    // Collate into stock ids 
    let stockIdList = panelItems.filter(panelItem => panelItem.stockId).map(panelItem => panelItem.stockId)
    stockIdList = _.uniq(stockIdList)

    stockIdList.forEach(stockId => {
      let stockItem = this.editorConfigurationData.stockList.find(stock => stock.id == stockId)
      if(!stockItem)
        return

      // Count number of panel items using this item
      let panelCount = panelItems.filter(panelItem => panelItem.stockId == stockId).length
      if(panelCount == 0)
        return

      this._createOrderItem(order, {
        parentOrderItemId: glassOrderItem.id,
        stockType: InventoryStockTypes.glassService,
        stockId: stockId,
        stockPriceGroupId: glassOrderItem.stockPriceGroupId,
        description: stockItem.description,
        quantity: glassOrderItem.quantity,
        units: panelCount,
        thickness: glassOrderItem.thickness
      }, {
        editorType: GlassDesignerTypes.EditorOrderItemDataTypes.panelItemService,
        stockId: stockId,
        glassShapeId: glassPanel.id
      })
    })
  }


  /**
   * Generate the services for stamped / not-stamped glass items
   */
  _generateStampServices(order, glassOrderItem, glassPanel) {
    var orderStampConfig = this.editorConfigurationData.orderStampConfig

    // If stamps not available for the glass item, don't do anything
    var isStampAvailable = FilterUtils.itemPassesFilter(orderStampConfig.priceGroupFilter, parseInt(glassOrderItem.stockPriceGroupId))
    if(!isStampAvailable)
      return

    if(glassPanel.isStamped) {
      // Stamp Required
      var stampRequiredStockItem = this.editorConfigurationData.stockList.find(stock => stock.id == orderStampConfig.stampRequiredStockId)
      if(stampRequiredStockItem) {

        this._createOrderItem(order, {
          parentOrderItemId: glassOrderItem.id,
          stockType: InventoryStockTypes.glassService,
          stockId: stampRequiredStockItem.id,
          stockPriceGroupId: glassOrderItem.stockPriceGroupId,
          description: stampRequiredStockItem.description,
          quantity: glassOrderItem.quantity,
          units: 1,
          thickness: glassOrderItem.thickness
        }, {
          editorType: GlassDesignerTypes.EditorOrderItemDataTypes.stampService,
          stockId: stampRequiredStockItem.id,
          glassShapeId: glassPanel.id
        })
      }      

    } else {
      // NO Stamp Required
      var stampNotRequiredStockItem = this.editorConfigurationData.stockList.find(stock => stock.id == orderStampConfig.stampNotRequiredStockId)
      if(stampNotRequiredStockItem) {

        this._createOrderItem(order, {
          parentOrderItemId: glassOrderItem.id,
          stockType: InventoryStockTypes.glassService,
          stockId: stampNotRequiredStockItem.id,
          stockPriceGroupId: glassOrderItem.stockPriceGroupId,
          description: stampNotRequiredStockItem.description,
          quantity: glassOrderItem.quantity,
          units: 1,
          thickness: glassOrderItem.thickness
        }, {
          editorType: GlassDesignerTypes.EditorOrderItemDataTypes.stampService,
          stockId: stampNotRequiredStockItem.id,
          glassShapeId: glassPanel.id
        })
      }
    }
  }


  /**
   * Generate the services for split lines on a glass panel
   */
  _generateSplitLineServices(order, glassOrderItem, glassPanel) {
    let configData = this.editorConfigurationData
    let splitRectangles = glassPanel.calculateSplittingRectangles()

    // Ignore if no splitting occurred
    if(splitRectangles.length == 0)
      return

    let rectDescList = splitRectangles.map(r => `${Math.round(r.max.y-r.min.y)}x${Math.round(r.max.x-r.min.x)}`)
    let splitList = _.uniq(rectDescList)

    let splitOutput = splitList.map(splitText => {
      return {
        quantity: rectDescList.filter(rectText => rectText == splitText).length,
        info: splitText
      }
    })

    let notes = splitOutput.map(split => {
      return `${split.quantity} @ ${split.info}`
    }).join(', ')

    // Append split info to the glass order item
    glassOrderItem.orderEditorNotes += `SPLIT TO: ${notes}.`

    // Figure out the stock service item
    let serviceStockId = glassPanel.getGlassType() == GlassTypes.annealed ? parseInt(configData.orderEditorSettings[AccountSettingKeys.annealedSplittingStockId] || 0) : parseInt(configData.orderEditorSettings[AccountSettingKeys.laminateSplittingStockId] || 0)
    let stockItem = this.editorConfigurationData.stockList.find(stock => stock.id == serviceStockId)
    if(stockItem == null)
      return

    // Note: Only 1 service item added per glass order item, regardless of number of splits
    this._createOrderItem(order, {
      parentOrderItemId: glassOrderItem.id,
      stockType: InventoryStockTypes.glassService,
      stockId: serviceStockId,
      stockPriceGroupId: glassOrderItem.stockPriceGroupId,
      description: stockItem.description,
      quantity: glassOrderItem.quantity,
      units: 1,
      thickness: glassOrderItem.thickness
    }, {
      editorType: GlassDesignerTypes.EditorOrderItemDataTypes.splitService,
      stockId: serviceStockId,
      glassShapeId: glassPanel.id
    }, {
      splitInfo: splitOutput
    })
  }


  /**
   * Find a cutout & stock item from the order editor configuration
   * If found, returns an object matching the OrderEditorCutoutStock model object on the server
   * 
   * @param {Object} cutoutFilterInfo - Properties about the cutout to filter by. Properties include:
   *    type - Type from OrderEditorCutoutTypesCutoutTypes
   *    geometryShapeType - For center cutouts, the geometry shape type from OrderEditorGeometryShapeTypes
   *    isCutoutPolished - Set if the cutout is polished
   *    isCountersunk - Set if the center cutout is countersunk
   *    countersunkRadius - The countersunk amount
   *    maxDimension - Largest/Maximum dimension of the cutout
   * 
   * @return {Object} If found, returns an object matching the OrderEditorCutoutStock model object on the server. Otherwise null if no stock found
   */
  _findCutoutStockItem(cutoutFilterInfo) {
    for(var cutoutInfo of this.editorConfigurationData.cutoutList) {
      // Cutout filters
      if(cutoutFilterInfo.type != cutoutInfo.type)
        continue
      if(cutoutFilterInfo.type == OrderEditorCutoutTypes.centerCutOut) {
        if(cutoutFilterInfo.geometryShapeType != cutoutInfo.geometryShapeType)
          continue
      }

      // Find a stock item in the cutout which passes all the tests
      for(var cutoutStockInfo of cutoutInfo.orderEditorCutoutStocks) {
        // Polished filter
        if(cutoutStockInfo.polishedFilterType == CutoutPolishFilterTypes.polishedOnly && !cutoutFilterInfo.isCutoutPolished)
          continue
        if(cutoutStockInfo.polishedFilterType == CutoutPolishFilterTypes.unpolishedOnly && cutoutFilterInfo.isCutoutPolished)
          continue

        // Countersunk filter
        if(cutoutStockInfo.hasCountersunkFilter) {
          if(!cutoutFilterInfo.isCountersunk)
            continue
          if(cutoutFilterInfo.countersunkRadius < cutoutStockInfo.countersunkFilterMin || cutoutFilterInfo.countersunkRadius >= cutoutStockInfo.countersunkFilterMax)
            continue
        } else {
          if(cutoutFilterInfo.isCountersunk)
            continue
        }

        // Dimension filter
        if(cutoutFilterInfo.maxDimension < cutoutStockInfo.dimensionFilterMin || cutoutFilterInfo.maxDimension >= cutoutStockInfo.dimensionFilterMax)
          continue

        // Found a match
        return cutoutStockInfo
      }
    }
      
    // No valid stock item found in the order editor cutouts list
    return null
  }


  /**
   * Find a shape group in the glass panel that contains links to segments in a ShapeCutout object
   * 
   * @param {GlassShape} glassPanel - Glass panel to search groups for
   * @param {ShapeCutout} shapeCutout - Cutout to look at segments in
   * @return {GlassShapeGroup} Returns the shape group if found, or null if no group associated with the cutout
   */
  _findShapeGroupForCNCCutout(glassPanel, shapeCutout) {
    // Ignore center/radius cuts
    if(shapeCutout.type != OrderEditorCutoutTypes.cornerCutOut && shapeCutout.type != OrderEditorCutoutTypes.edgeCutOut)
      return null

    let groupList = glassPanel.getShapeGroupList()

    return groupList.find(shapeGroup => {

      // Segment in the cutout exists in the group?
      let matchingSegId = shapeCutout.segmentIdList.find(segmentId => {
        return shapeGroup.isSegmentInGroup(segmentId)
      })

      return matchingSegId != null
    })
  }


  /**
   * Calculate the pricing details for all the order items
   * Calculates line prices, tax percentages
   * Note: We don't worry about min charge groups & min invoice items as the order item list handles all that
   * 
   */
  _calculatePricing(order) {
    order.totalTax = 0
    order.totalPrice = 0

    order.orderItems.forEach(orderItem => {
      let parentItem = this._getOrderItemById(order, orderItem.parentOrderItemId)

      if(!orderItem.isMinChargeDisabled) {
        if(orderItem.units < orderItem.minChargeQuantity)
          orderItem.unitsNotice = 'Units set to minimum of ' + Utils.formatDecimal(orderItem.minChargeQuantity, 3) + ' per item'
      }

      // If percentOfLineItem uom type, then set units to price of parent item
      if(parentItem && orderItem.stock.uomType == UOMTypes.percentOfLineItem) {
        let parentItemAmount = Utils.roundCurrency(Math.max(parentItem.units, parentItem.minChargeQuantity) * Utils.roundCurrency(parentItem.unitPrice))
        orderItem.units = parentItemAmount / 100.0
      }

      let minChargeQuantity = orderItem.isMinChargeDisabled ? 0 : orderItem.minChargeQuantity
      orderItem.itemPrice = Utils.roundSubTotalCurrency(Math.max(orderItem.units, minChargeQuantity) * Utils.roundCurrency(orderItem.unitPrice))
    })

    // Update order item groups for minimum charge
    this._updateOrderItemGroups(order)

    // Update regular prices first
    for(let orderItem of order.orderItems) {
      if(orderItem.stock.uomType == UOMTypes.percentOfOrder)
        continue

      this._recalculateOrderItemPrice(order, orderItem)
    }

    // Update percentOfOrder order items last
    for(let orderItem of order.orderItems) {
      if(orderItem.stock.uomType != UOMTypes.percentOfOrder)
        continue

      this._recalculateOrderItemPrice(order, orderItem)
    }
  }


  /**
   * Update the item groups in the order item list
   * Sets the min charge / total price details for each group
   * 
   * @param {Order} order - Order containing the order items
   */
  _updateOrderItemGroups(order) {
    order.orderItems.forEach(orderItem => {
      // Only process root items
      if(orderItem.parentOrderItemId != null)
        return

      var groupOrderItems = this._getOrderItemsInGroup(order, orderItem)

      var groupMinChargePrice = 0
      var groupTotalItemPrice = 0

      for(let groupItem of groupOrderItems) {
        // Ignore min price charge items
        if(groupItem.isMinPriceItem || groupItem.isMinInvoiceItem)
          continue

        // Single item min-price order items DO count towards the total group min charge price
        // BUT NOT the min charge item value
        if(!groupItem.isMinChargeDisabled && !groupItem.isMinPriceSingleItem)
          groupMinChargePrice = Utils.roundCurrency(Math.max(groupMinChargePrice, groupItem.minChargePrice))

        groupTotalItemPrice = Utils.roundCurrency(groupTotalItemPrice + groupItem.itemPrice)
      }

      // Update all items
      for(let groupItem of groupOrderItems) {
        groupItem.groupMinChargePrice = groupMinChargePrice
        groupItem.groupTotalItemPrice = groupTotalItemPrice
      }
    })
  }


  /**
   * Recalculate the order item pricing in an order
   */
  _recalculateOrderItemPrice(order, orderItem) {
    let rootItem = this._findOrderItemRoot(order, orderItem)

    // Don't update pricing on certain items
    if(rootItem && (rootItem.isCancelled || rootItem.isCancelFeeItem))
      return

    if(orderItem.stockType == InventoryStockTypes.glass || orderItem.stockType == InventoryStockTypes.general) {

      // Handle percentOfOrder uom type
      // Note: These order items need to be calculated after the other order items have their linePrice's calculations done
      if(orderItem.stock.uomType == UOMTypes.percentOfOrder) {
        let orderTotalPrice = order.orderItems.reduce((curTotal, item) => {
          // Ignore this & other percentOfOrder items
          if(item.id == orderItem.id || item.stock.uomType == UOMTypes.percentOfOrder)
            return curTotal

          // Ignore child items of percentOfOrder (eg, minPriceItems)
          if(item.parentOrderItemId) {
            let parentItem = order.orderItems.find(listItem => listItem.id == item.parentOrderItemId)
            if(parentItem && parentItem.stock.uomType == UOMTypes.percentOfOrder)
              return curTotal
          }

          return Utils.roundCurrency(curTotal + item.linePrice)
        }, 0)
        
        orderItem.units = Utils.roundNumber(orderTotalPrice / 100.0, 4)
        orderItem.itemPrice = Utils.roundSubTotalCurrency(Math.max(orderItem.units, orderItem.minChargeQuantity) * Utils.roundCurrency(orderItem.unitPrice))
      }
    }

    // If a discount is applied, flag a price warning for approval
    if(orderItem.discountRate > 0)
      orderItem.priceError += 'Discount applied to item\n'

    // Final discount & line price calculations
    var itemLineAmount = Utils.roundCurrency(orderItem.itemPrice * orderItem.quantity)

    if(orderItem.discountRate > 0) {
      orderItem.discountAmount = Utils.roundCurrency(currency(itemLineAmount, { precision: 4}).multiply(orderItem.discountRate).multiply(0.01).value)
    }

    orderItem.linePrice = Utils.roundCurrency(itemLineAmount - orderItem.discountAmount)
  }



  /**
   * Calculate the order totals based on all order items
   * 
   * @param {Order} order - Order being calculated
   */
  _calculateOrderTotals(order) {
    var orderTotalSubTotal = order.orderItems.reduce((curTotal, item) => { return Utils.roundCurrency(curTotal + item.linePrice) }, 0)
    var orderTotalTax = Utils.roundCurrency(order.orderItems.reduce((curTax, item) => {
      return curTax.add(currency(item.linePrice, { precision: 4}).multiply(item.taxRatePercent).multiply(0.01))
      
    }, currency(0, { precision: 4 })).value)
    var orderTotalPrice = orderTotalSubTotal + orderTotalTax

    // Non Chargeable Rework
    if(order.type == OrderTypes.nonChargeableRework) {
      // Zero all pricing for NCR order types
      orderTotalSubTotal = 0
      orderTotalTax = 0
      orderTotalPrice = 0
    }

    order.totalTax = orderTotalTax
    order.totalPrice = orderTotalPrice
  }


  /**
   * Calculate the production editor errors for a glass panel
   * Generates the string & 
   */
  _calculateProductionEditorErrors(glassPanel) {
    let warningList = this.editor.getGuidelineHelper().getWarningsByShapeId(glassPanel.id)

    return warningList.map(guidelineWarning => {
      return guidelineWarning.toHTMLString()
    }).join('\n')
  }



  /**
   * Get an order item given an ID
   * 
   * @param {Order} order - Order containing the order items
   * @return {OrderItem} Returns the OrderItem instance for the ID, or null if not found
   */
  _getOrderItemById(order, id) {
    return order.orderItems.find(orderItem => orderItem.id == id)
  }


  /**
   * Get a list of all OrderItems that are in a group of the root item
   * Gets all children at all levels recursively
   * 
   * @param {Order} order - Order containing the order items
   * @param {OrderItem} rootItem - root OrderItem to get all children items for in the group
   * @return {Array<OrderItem>} Returns the flattened array of OrderItems in the group
   */
  _getOrderItemsInGroup(order, rootItem) {
    if(rootItem == null)
      return []
    var outputList = [rootItem]

    order.orderItems.forEach(item => {
      if(item.parentOrderItemId == rootItem.id) {
        
        let childList = this._getOrderItemsInGroup(order, item)
        outputList = outputList.concat(childList)
      }
    })

    return outputList
  }


  /**
   * Find an OrderItem's root item in a flattened OrderItem list
   * If the provided item is the root item, it'll return that
   * 
   * @param {Order} order - Order containing the order items
   * @param {OrderItem} item - Item to search the root item this item belongs too
   * @return {OrderItem} Returns the root OrderItem model object
   */
  _findOrderItemRoot(order, item) {
    if(item.parentOrderItemId == null)
      return item

    let parentItem = order.orderItems.find(listItem => listItem.id == item.parentOrderItemId)
    if(!parentItem)
      return item
    
    return this._findOrderItemRoot(order, parentItem)
  }


  /**
   * Generate an new order item
   * Sets default properties on the order item
   *
   * @return {Object} Returns a new object containing default OrderItem model object
   */
  _createOrderItem(order, defaults = {}, editorDataFilter = {}, additionalEditorData = {}) {
    let editorFilterKeys = Object.keys(editorDataFilter)

    var orderItemData = _.cloneDeep(defaults)

    // Setup all the defaults for the order item
    // Note: Any items not generated by the editor & we need to import from order item list, don't add here
    _.defaults(orderItemData, {
      parentOrderItemId: null,
      stockType: InventoryStockTypes.glass,
      isProcessedItem: false,
      isSplitSheetItem: false,
      isEditorGeneratedItem: false,
      isMinPriceItem: false,
      isMinPriceSingleItem: false,
      isMinChargeDisabled: false,
      description: '',
      generatedDescription: '',
      mark: '',
      notes: '',
      orderEditorNotes: '',
      quantity: 1,
      units: 0,
      taxRateId: null,
      taxRatePercent: 0,
      linePrice: 0,
      unitPriceOverridden: false,
      unitPrice: 0,
      itemPrice: 0,
      minChargeInvoice: 0,
      minChargePrice: 0,
      minChargeQuantity: 0,
      groupMinChargePrice: 0,
      groupTotalItemPrice: 0,
      unitsNotice: '',
      priceNotice: '',
      priceError: '',
      productionError: '',
      productionConfigError: '',
      productionStockError: '',
      productionEditorError: '',
      glassHeight: 0,
      glassWidth: 0,
      glassLevelId: null,
      edgeworkFlagList: [],
      thickness: 0,
      grainDirection: null,
      stockFaceSideUpType: null,
      stock: {},
      stockPriceGroup: {},
      servicePriceRange: {},
      editorData: {},
      shapeData: {},
      shapeDataHash: null,
      glassLevel: {}
    })
    
    // Attempt to find the existing order item using the shape data filter
    let orderItemList = this.releasedOrderItemList
    var existingItem = orderItemList.find(orderItem => {
      if(!orderItem.editorData || !orderItem.isEditorGeneratedItem)
        return false
      if(editorFilterKeys.length == 0)
        return false

      let matchingCount = editorFilterKeys.filter(key => {
        return editorDataFilter[key] == orderItem.editorData[key]
      }).length

      return matchingCount == editorFilterKeys.length
    })

    if(!existingItem) {
      existingItem = new OrderItem(orderItemData)

      // New item defaults
      existingItem.isExternal = false
      existingItem.requiresCustomerApproval = false
      existingItem.discountRate = 0
      existingItem.discountAmount = 0
      existingItem.itemPrice = 0

      order.addOrderItem(existingItem)

    } else {
      // Update item with the data
      Object.keys(orderItemData).forEach(key => {
        existingItem[key] = orderItemData[key]
      })

      // Remove from released list & re-add the order item back
      this.releasedOrderItemList = this.releasedOrderItemList.filter(releasedItem => releasedItem.id != existingItem.id)
      order.addOrderItem(existingItem)
    }

    // Fill in the editor data filter fields
    Object.keys(editorDataFilter).forEach(key => {
      existingItem.editorData[key] = editorDataFilter[key]
    })

    // Fill in the additional editor data used internally
    if(additionalEditorData) {
      Object.keys(additionalEditorData).forEach(key => {
        existingItem.editorData[key] = additionalEditorData[key]
      })
    }

    // All order items generated by the editor need this flag set
    existingItem.isEditorGeneratedItem = true

    // Fill in the additional info
    let stockPriceGroup = this.editorConfigurationData.stockPriceGroupList.find(stockPriceGroup => stockPriceGroup.id == orderItemData.stockPriceGroupId)
    if(stockPriceGroup)
      existingItem.stockPriceGroup = _.clone(stockPriceGroup)

    // Sales tax rate
    if(this.editorConfigurationData.salesTaxRate) {
      existingItem.taxRateId = this.editorConfigurationData.salesTaxRate.id
      existingItem.taxRatePercent = parseFloat(this.editorConfigurationData.salesTaxRate.rate || 0)
    }

    // Fill in stock info
    let stockInfo = this.editorConfigurationData.stockList.find(stock => stock.id == orderItemData.stockId)
    if(stockInfo) {
      existingItem.stock.id = stockInfo.id
      existingItem.stock.code = stockInfo.code
      existingItem.stock.name = stockInfo.name
      existingItem.stock.uomType = stockInfo.uomType
      existingItem.stock.thickness = parseFloat(stockInfo.thickness || 0)
    }

    return existingItem    
  }


  /**
   * Generate the SVG display data field for a glass OrderItem based on the glass panel
   * Fills in the svgDisplayData field
   */
  _generateGlassItemSVGDisplayData(glassOrderItem, glassPanel) {
    // Convert to SVG & store in the glass order item
    let svgHelper = new SVGHelper(this.editor)
    svgHelper.setPadding(35, 35, 35, 35)
    glassOrderItem.svgDisplayData = svgHelper.generateGlassShapeSVG(glassPanel, {
      showDimensions: true,
      showOverallDimensions: true,
      showEdgeworkLegend: true,
      showStampLegend: true,
      showSquareCorners: true,
      showEdgeworkHighlights: true,
      showPanelItems: true,
      showRunoutLegend: true,
      showAnchors: true,
      showBorder: true,
      svgHeight: '240px',
      scaleMaxWidth: 720,
      scaleMaxHeight: 240
    })

    // Generate the label SVG with less info
    // Set less padding for label SVG
    svgHelper.setPadding(1, 1, 1, 1)
    glassOrderItem.svgLabelData = svgHelper.generateGlassShapeSVG(glassPanel, {
      showDimensions: false,
      showOverallDimensions: false,
      showEdgeworkLegend: false,
      showSquareCorners: true,
      centerDiagram: true,
      svgHeight: '240px',
      scaleMaxWidth: 240,
      scaleMaxHeight: 240
    })

    // Generate the label SVG corner data
    // Shows square corners & stamp position
    glassOrderItem.svgLabelCornerData = svgHelper.generateGlassCornerDataSVG(glassPanel, {
      svgHeight: '240px',
      scaleMaxWidth: 240,
      scaleMaxHeight: 240
    })
  }


  /**
   * Generate the SVG display data field for an assembly OrderItem based on the assembly group
   * Fills in the svgDisplayData field
   */
  _generateAssemblyItemSVGDisplayData(assemblyOrderItem, assemblyGroup) {
    // Convert to SVG & store in the assembly order item
    let svgHelper = new SVGHelper(this.editor)
    svgHelper.setPadding(35, 35, 35, 35)
    assemblyOrderItem.svgDisplayData = svgHelper.generateAssemblyShapeSVG(assemblyGroup, {
      showDimensions: true,
      showOverallDimensions: false,
      showEdgeworkLegend: true,
      showStampLegend: true,
      showSquareCorners: true,
      showEdgeworkHighlights: true,
      showSideView: true,
      showPanelItems: true,
      showRunoutLegend: true,
      showAnchors: true,
      showBorder: true,
      svgHeight: '240px',
      scaleMaxWidth: 720,
      scaleMaxHeight: 240
    })

    // Do a simple SVG based on largest panel size
    var assemblySize = assemblyGroup.calculateLargestSize()
    svgHelper.setPadding(1, 1, 1, 1)
    assemblyOrderItem.svgLabelData = svgHelper.generateRectangleSVG({
      width: assemblySize.width,
      height: assemblySize.height,
      edgeworkDetailsList: [],
      childOrderItems: []
    }, {
      showDimensions: false,
      showEdgeworkLegend: false,
      showSquareCorners: true,
      centerDiagram: true,
      svgHeight: '240px',
      scaleMaxWidth: 240,
      scaleMaxHeight: 240
    })

  }


  /**
   * Calculate the review screen approval list for the panels
   */
  _calculateReviewApprovalItems(order) {
    let reviewItemList = order.orderItems.filter(orderItem => {
      return orderItem.isEditorGeneratedItem && orderItem.editorData && 
        (orderItem.editorData.editorType == GlassDesignerTypes.EditorOrderItemDataTypes.glassShape || orderItem.editorData.editorType == GlassDesignerTypes.EditorOrderItemDataTypes.assemblyGroup)
    })

    reviewItemList.forEach(orderItem => {

      if(orderItem.editorData.editorType == GlassDesignerTypes.EditorOrderItemDataTypes.glassShape) {
        this._calculateGlassShapeReviewDetails(order, orderItem)
      }
      if(orderItem.editorData.editorType == GlassDesignerTypes.EditorOrderItemDataTypes.assemblyGroup) {
        this._calculateAssemblyGroupReviewDetails(order, orderItem)
      }
    })
  }


  /**
   * Calculate the review details for a glass shape order item
   */
  _calculateGlassShapeReviewDetails(order, orderItem) {
    var isStockLinkedItem = orderItem.stockPriceGroup && orderItem.stockPriceGroup.isStockLinked
    var glassShape = this.editor.getObjectById(orderItem.editorData.glassShapeId)
    if(!glassShape)
      return

    var approvals = []
    var panelItem = {
      name: 'Panel',
      approved: false,
      items: [
        { name: 'Size', value: `${orderItem.glassHeight}mm x ${orderItem.glassWidth}mm` },
        { name: 'Quantity', value: `${orderItem.quantity}` },
        { name: 'Mark', value: `${this.formatEmptyReviewValue(orderItem.mark)}` }
      ]
    }
    // Double sided / Grain direction info
    if(glassShape.isStockUsingFaces()) {
      panelItem.items.push({
        name: 'Face Side Up', value: Utils.getLookupName(StockFaceTypeLookup, glassShape.itemData.stockFaceSideUpType)
      })
    }
    if(glassShape.isStockUsingGrain()) {
      panelItem.items.push({
        name: 'Grain', value: Utils.getLookupName(StockGrainDirectionTypeLookup, glassShape.itemData.grainDirection)
      })
    }
    
    approvals.push(panelItem)

    approvals.push({
      name: 'Stock',
      approved: false,
      items: [
        { name: 'Stock', value: `${orderItem.stock.name}` },
        { name: 'Price Group', value: `${orderItem.stockPriceGroup.name}` },
      ]
    })         

    if(!isStockLinkedItem) {
      var edgeworkItems = this.getChildOrderItemsWithEditorType(order.orderItems, orderItem, GlassDesignerTypes.EditorOrderItemDataTypes.edgeworkService)
      let approvalItems = []

      edgeworkItems.forEach(edgeworkOrderItem => {
        if(_.isArray(edgeworkOrderItem.editorData.cutLineList)) {
          edgeworkOrderItem.editorData.cutLineList.forEach(cutLineInfo => {
            approvalItems.push({
              value: `${edgeworkOrderItem.description} - ${Utils.formatDecimal(Utils.roundQuantityValue(cutLineInfo.length), 2)}m`,
              bounds: cutLineInfo.bounds
            })
          })
        }
      })

      approvals.push({
        name: 'Edgework',
        approved: false,
        hideEmpty: false,
        items: approvalItems
      })

      var cutoutItems = this.getChildOrderItemsWithEditorType(order.orderItems, orderItem, GlassDesignerTypes.EditorOrderItemDataTypes.cutoutService)

      var cutoutTypes = [OrderEditorCutoutTypes.cornerCutOut, OrderEditorCutoutTypes.edgeCutOut, OrderEditorCutoutTypes.centerCutOut, OrderEditorCutoutTypes.radiusCut]
      cutoutTypes.forEach(cutoutType => {
        var cutoutTypeItems = cutoutItems.filter(cutoutItem => cutoutItem.editorData.cutoutType == cutoutType)
        let approvalItems = []

        cutoutTypeItems.forEach(cutoutItem => {
          if(_.isArray(cutoutItem.editorData.cutoutBoundsList)) {
            cutoutItem.editorData.cutoutBoundsList.forEach((cutoutBounds, subItemIndex) => {

              let detailsList = []
              if(_.isArray(cutoutItem.editorData.cutoutDetailsItemList))
                detailsList = cutoutItem.editorData.cutoutDetailsItemList[subItemIndex]

              approvalItems.push({
                value: cutoutItem.description,
                bounds: cutoutBounds,
                detailsList: detailsList
              })
            })
          }
        })

        approvals.push({
          name: Utils.getLookupName(OrderEditorCutoutTypeLookup, cutoutType) + 's',
          approved: false,
          hideEmpty: true,
          items: approvalItems
        })
      })

      var runouts = glassShape.calculateRunoutList()
      approvals.push({
        name: 'Runouts',
        approved: false,
        hideEmpty: true,
        items: runouts.map(runout => {
          return {
            value: runout.description,
            bounds: runout.bounds
          }
        })
      })

      // Other Services
      var otherItems = this.getChildOrderItemsWithEditorType(order.orderItems, orderItem, [
        GlassDesignerTypes.EditorOrderItemDataTypes.shapeService,
        GlassDesignerTypes.EditorOrderItemDataTypes.panelItemService,
        GlassDesignerTypes.EditorOrderItemDataTypes.stampService,
        GlassDesignerTypes.EditorOrderItemDataTypes.customItem
      ])

      approvals.push({
        name: 'Other',
        approved: false,
        hideEmpty: true,
        items: otherItems.map(otherItem => {
          return {
            value: `${otherItem.units}x ${otherItem.description}`
          }
        })
      })

    } else {
      // Splits
      var splitItems = this.getChildOrderItemsWithEditorType(order.orderItems, orderItem, GlassDesignerTypes.EditorOrderItemDataTypes.splitService)
      approvals.push({
        name: 'Splits',
        approved: false,
        hideEmpty: true,
        items: _.compact(splitItems.map(splitItem => {
          if(!splitItem.editorData || !_.isArray(splitItem.editorData.splitInfo))
            return null

          return splitItem.editorData.splitInfo.map(split => {
            return { value: `${split.quantity}x ${split.info}` }
          })
        })).flat(1)
      })
    }

    // Cleanup
    approvals = approvals.filter(approval => {
      return (!approval.hideEmpty || approval.items.length > 0)
    })
    approvals.forEach(approval => {
      if(approval.items.length == 0)
        approval.items = [{ value: 'None' }]

      approval.approved = false
      approval.key = `${approval.name}-${approval.items.length}`

      // Get existing ticked items for the approvals stored in the shape
      var existingApproval = glassShape.reviewApprovalList.find(curApproval => approval.key == curApproval.key)
      if(existingApproval && existingApproval.approved)
        approval.approved = true
      else if(glassShape.reviewApprovalOverride)
        approval.approved = true

      approval.items.forEach(approvalItem => {
        approvalItem.approved = false
        approvalItem.key = `${approval.name}-${approvalItem.name}`

        // Match sub items
        if(existingApproval) {
          var existingSubItem = existingApproval.items.find(curSubItem => curSubItem.key == approvalItem.key)
          if(existingSubItem && existingSubItem.approved)
            approvalItem.approved = true
        }

        if(glassShape.reviewApprovalOverride)
          approvalItem.approved = true
      })
    })

    glassShape.updateReviewApprovals(approvals)
  }


  /**
   * Calculate the review details for an assembly group order item
   */
  _calculateAssemblyGroupReviewDetails(order, orderItem) {
    var assemblyGroup = this.editor.getObjectById(orderItem.editorData.assemblyGroupId)
    if(!assemblyGroup)
      return

    var assemblySize = assemblyGroup.calculateLargestSize()

    var approvals = []
    var assemblyItem = {
      name: 'Assembly',
      approved: false,
      items: [
        { name: 'Size', value: `${assemblySize.height}mm x ${assemblySize.width}mm` },
        { name: 'Quantity', value: `${orderItem.quantity}` },
        { name: 'Mark', value: `${this.formatEmptyReviewValue(orderItem.mark)}` }
      ]
    }
    
    approvals.push(assemblyItem) 

    approvals.push({
      name: 'Stock',
      approved: false,
      items: [
        { name: 'Stock', value: `${orderItem.stock.name}` }
      ]
    })

    var assemblyLayerList = assemblyGroup.generateLayerList()

    approvals.push({
      name: 'Layers',
      approved: false,
      items: assemblyLayerList.map((layer, layerIndex) => {
        return {
          name: `Layer ${layerIndex+1}`,
          value: layer.name
        }
      })
    })

    // Cleanup
    approvals = approvals.filter(approval => {
      return (!approval.hideEmpty || approval.items.length > 0)
    })
    approvals.forEach(approval => {
      if(approval.items.length == 0)
        approval.items = [{ value: 'None' }]

      approval.approved = false
      approval.key = `${approval.name}-${approval.items.length}`

      // Get existing ticked items for the approvals stored in the shape
      var existingApproval = assemblyGroup.reviewApprovalList.find(curApproval => approval.key == curApproval.key)
      if(existingApproval && existingApproval.approved)
        approval.approved = true
      else if(assemblyGroup.reviewApprovalOverride)
        approval.approved = true

      approval.items.forEach(approvalItem => {
        approvalItem.approved = false
        approvalItem.key = `${approval.name}-${approvalItem.name}`

        // Match sub items
        if(existingApproval) {
          var existingSubItem = existingApproval.items.find(curSubItem => curSubItem.key == approvalItem.key)
          if(existingSubItem && existingSubItem.approved)
            approvalItem.approved = true
        }

        if(assemblyGroup.reviewApprovalOverride)
          approvalItem.approved = true
      })
    })

    assemblyGroup.updateReviewApprovals(approvals)
  }


  getChildOrderItemsWithEditorType(orderItems, parentOrderItem, editorType = []) {

    if(!_.isArray(editorType))
      editorType = [editorType]

    return orderItems.filter(orderItem => {
      return orderItem.parentOrderItemId == parentOrderItem.id &&
        orderItem.editorData &&
        editorType.indexOf(orderItem.editorData.editorType) != -1
    })
  }

  formatEmptyReviewValue(value) {
    if(value == null || value == '')
      return 'None'
    return value
  }
}
